const toCount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeText = (value) => String(value || '').trim();

const clientDisplayName = (client) => (
  normalizeText(client?.name)
  || normalizeText(client?.email)
  || `User ${client?.id || ''}`.trim()
);

const getCompanyName = (companyLike) => (
  normalizeText(companyLike?.company_display_name)
  || normalizeText(companyLike?.display_name)
  || normalizeText(companyLike?.company_name)
  || normalizeText(companyLike?.companyName)
  || ''
);

const getCompanyAdminLabel = (companyLike) => (
  normalizeText(companyLike?.company_admin_alias)
  || normalizeText(companyLike?.admin_alias)
  || getCompanyName(companyLike)
);

const hasCompanyIdentity = (companyLike) => (
  companyLike?.company_id != null
  || Boolean(getCompanyName(companyLike))
);

const hasActiveZips = (companyLike) => (
  toCount(companyLike?.active_zip_count) > 0
  || companyLike?.has_active_zips === true
  || companyLike?.coverage_status === 'active'
);

const hasDemoZips = (companyLike) => (
  toCount(companyLike?.demo_zip_count) > 0
  || companyLike?.has_demo_zips === true
  || companyLike?.coverage_status === 'demo'
);

const sortByLabel = (left, right) => (
  clientDisplayName(left).localeCompare(clientDisplayName(right), undefined, { sensitivity: 'base' })
);

const getClientMemberships = (client) => (
  Array.isArray(client?.company_memberships)
    ? client.company_memberships.filter(hasCompanyIdentity)
    : (hasCompanyIdentity(client) ? [client] : [])
);

const addCompanyClient = (groupsByKey, companyLike, client) => {
  const companyName = getCompanyName(companyLike) || 'Unknown company';
  const companyAdminLabel = getCompanyAdminLabel(companyLike) || companyName;
  const key = companyLike?.company_id != null
    ? `company:${companyLike.company_id}`
    : `company-name:${companyAdminLabel.toLowerCase()}`;

  if (!groupsByKey.has(key)) {
    groupsByKey.set(key, {
      key,
      companyId: companyLike?.company_id ?? null,
      companyName,
      companyAdminLabel,
      activeZipCount: toCount(companyLike?.active_zip_count),
      demoZipCount: toCount(companyLike?.demo_zip_count),
      coverageStatus: companyLike?.coverage_status || 'none',
      clients: [],
    });
  }

  const group = groupsByKey.get(key);
  group.activeZipCount = Math.max(group.activeZipCount || 0, toCount(companyLike?.active_zip_count));
  group.demoZipCount = Math.max(group.demoZipCount || 0, toCount(companyLike?.demo_zip_count));
  if (hasActiveZips(companyLike)) {
    group.coverageStatus = 'active';
  } else if (hasDemoZips(companyLike) && group.coverageStatus !== 'active') {
    group.coverageStatus = 'demo';
  }
  if (!group.clients.some((existing) => String(existing?.id) === String(client?.id))) {
    group.clients.push(client);
  }
};

const sortedCompanyGroups = (groupsByKey) => (
  Array.from(groupsByKey.values())
    .map((group) => ({
      ...group,
      clients: group.clients.slice().sort(sortByLabel),
    }))
    .sort((left, right) => (left.companyAdminLabel || left.companyName).localeCompare((right.companyAdminLabel || right.companyName), undefined, { sensitivity: 'base' }))
);

export const formatAdminClientOptionLabel = (client, { includeEmail = false, includeCompany = false } = {}) => {
  const name = clientDisplayName(client);
  const email = normalizeText(client?.email);
  const userLabel = includeEmail && email && email !== name ? `${name} (${email})` : name;

  if (!includeCompany) {
    return userLabel;
  }

  const companyName = getCompanyAdminLabel(client);
  return companyName ? `${companyName} - ${userLabel}` : userLabel;
};

export const formatAdminCompanyGroupLabel = (group) => {
  const label = group.companyAdminLabel || group.companyName;
  if (group.coverageStatus === 'active' && group.activeZipCount > 0) {
    return `${label} (${group.activeZipCount} active ZIPs)`;
  }
  if (group.coverageStatus === 'demo' && group.demoZipCount > 0) {
    return `${label} (${group.demoZipCount} demo ZIPs)`;
  }
  return label;
};

export const buildAdminClientGroups = (clients, currentUserId) => {
  const safeClients = Array.isArray(clients) ? clients : [];
  const currentUserIdString = currentUserId != null ? String(currentUserId) : null;
  const mainUser = currentUserIdString
    ? safeClients.find((client) => String(client?.id) === currentUserIdString) || null
    : null;

  const subscriberCompanies = new Map();
  const demoCompanies = new Map();
  const restCompanies = new Map();
  const unassignedUsers = [];

  safeClients.forEach((client) => {
    if (!client || (currentUserIdString && String(client.id) === currentUserIdString)) {
      return;
    }

    const memberships = getClientMemberships(client);
    const activeMemberships = memberships.filter(hasActiveZips);
    if (activeMemberships.length > 0) {
      activeMemberships.forEach((membership) => addCompanyClient(subscriberCompanies, membership, client));
      return;
    }

    const demoMemberships = memberships.filter(hasDemoZips);
    if (demoMemberships.length > 0) {
      demoMemberships.forEach((membership) => addCompanyClient(demoCompanies, membership, client));
      return;
    }

    if (memberships.length > 0) {
      memberships.forEach((membership) => addCompanyClient(restCompanies, membership, client));
      return;
    }

    unassignedUsers.push(client);
  });

  return {
    mainUser,
    subscriberCompanies: sortedCompanyGroups(subscriberCompanies),
    demoCompanies: sortedCompanyGroups(demoCompanies),
    restCompanies: sortedCompanyGroups(restCompanies),
    unassignedUsers: unassignedUsers.sort(sortByLabel),
  };
};
